import React from 'react';
import { useTranslation } from 'react-i18next';
import { UseCasesMessages } from './UseCases.messages';
import { WebGlobeIcon, EmailIcon, ToolsIcon } from '../../design-system/components/SvgIcons';

interface UseCasesProps {
  /*
   * Which heading element the section title renders as.
   *
   * On the homepage this is a section below the page h1, so h2 is right. On
   * /use-cases it is the top of the page, and that page had no h1 at all — the
   * outline started at h2, on a route the sitemap gives priority 0.9, in all 8
   * locales. Rather than adding a second heading above this one and repeating the
   * same words, the section title becomes the page title where it is the page.
   */
  headingLevel?: 1 | 2;
}

export const UseCases: React.FC<UseCasesProps> = ({ headingLevel = 2 }) => {
  const { t } = useTranslation();
  const Heading = (headingLevel === 1 ? 'h1' : 'h2') as 'h1' | 'h2';
  const useCases = [
    {
      title: t(UseCasesMessages.APP_LOCALIZATION_TITLE),
      description: t(UseCasesMessages.APP_LOCALIZATION_DESC),
      icon: <WebGlobeIcon size={28} />,
      codeExample: {
        title: t(UseCasesMessages.APP_LOCALIZATION_CODE_TITLE),
        before: `{
  "welcome": "Welcome back, \${userName}!",
  "notifications": "You have {count} new {count, plural, one {message} other {messages}}",
  "checkout": "Total: %s items for $%.2f"
}`,
        after: `// Spanish translation - placeholders preserved
{
  "welcome": "¡Bienvenido de nuevo, \${userName}!",
  "notifications": "Tienes {count} {count, plural, one {mensaje nuevo} other {mensajes nuevos}}",
  "checkout": "Total: %s artículos por $%.2f"
}`
      }
    },
    {
      title: t(UseCasesMessages.TRANSACTIONAL_TITLE),
      description: t(UseCasesMessages.TRANSACTIONAL_DESC),
      icon: <EmailIcon size={28} />,
      codeExample: {
        title: t(UseCasesMessages.TRANSACTIONAL_CODE_TITLE),
        before: `const emailTemplate = {
  subject: "Your order #\${orderId} has shipped!",
  body: "Hi \${customerName}, your order of \${itemCount} items will arrive on \${deliveryDate}."
};`,
        after: `// French translation - variables intact
const emailTemplate = {
  subject: "Votre commande #\${orderId} a été expédiée !",
  body: "Bonjour \${customerName}, votre commande de \${itemCount} articles arrivera le \${deliveryDate}."
};`
      }
    },
    {
      title: t(UseCasesMessages.DEVELOPER_TOOLING_TITLE),
      description: t(UseCasesMessages.DEVELOPER_TOOLING_DESC),
      icon: <ToolsIcon size={28} />,
      codeExample: {
        title: t(UseCasesMessages.DEVELOPER_TOOLING_CODE_TITLE),
        before: `// Original API strings
{
  "error.validation": "Field '%s' is required",
  "success.created": "Created %d records in %s",
  "info.progress": "Processing... {progress}% complete"
}`,
        after: `// German translation - format specifiers preserved
{
  "error.validation": "Feld '%s' ist erforderlich",
  "success.created": "%d Datensätze in %s erstellt",
  "info.progress": "Verarbeitung... {progress}% abgeschlossen"
}`
      }
    }
  ];

  return (
    <section className="use-cases">
      <div className="container">
        <div className="section-header">
          <Heading>{t(UseCasesMessages.TITLE)}</Heading>
          <p className="section-subtitle">
            {t(UseCasesMessages.SUBTITLE)}
          </p>
        </div>
        
        <div className="use-cases-grid">
          {useCases.map((useCase, index) => (
            <div key={index} className="use-case-card">
              <div className="use-case-header">
                <div className="use-case-icon" aria-hidden="true">
                  {useCase.icon}
                </div>
                <div className="use-case-info">
                  <h3 className="use-case-title">{useCase.title}</h3>
                  <p className="use-case-description">{useCase.description}</p>
                </div>
              </div>
              
              <div className="code-example">
                <h4 className="code-title">{useCase.codeExample.title}</h4>
                
                <div className="code-comparison">
                  <div className="code-block">
                    <div className="code-label">{t(UseCasesMessages.CODE_LABEL_ORIGINAL)}</div>
                    <pre className="code-content">
                      <code>{useCase.codeExample.before}</code>
                    </pre>
                  </div>
                  
                  <div className="code-arrow" aria-hidden="true">→</div>
                  
                  <div className="code-block">
                    <div className="code-label">{t(UseCasesMessages.CODE_LABEL_TRANSLATED)}</div>
                    <pre className="code-content">
                      <code>{useCase.codeExample.after}</code>
                    </pre>
                  </div>
                </div>
                
                <div className="placeholder-highlight">
                  <span className="highlight-badge">{t(UseCasesMessages.PLACEHOLDERS_PRESERVED)}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
